// OverlayEditor (`/creatives/:id`) — pixel-perfect editor for text-overlay
// tiles. Renders the SAME JsonSocialPost (the stored colored template + slots
// over the text-free scene) that produced the My Creatives thumbnail, so the
// editor view IS the thumbnail — one renderer, zero translation, no drift.
//
// UI matches the product-shot editor (StudioFinishEditor): an 843px stage on
// the left + a 589px controls panel on the right (layer rail | property panel),
// with a Save/Download action row. Used ONLY for tiles whose post.text_overlay
// is set (brand-setup / AS top-up with the text-overlay flag on); every other
// tile keeps StudioFinishEditor.
//
// 11 features: anchor-to-canvas · font family · size · weight · opacity ·
// text color (manual wins via lockColor) · align + italic · uppercase ·
// add CTA (defaults to Outline) · add text · show layers · edit text. Save
// re-bakes from the (possibly element-edited) template; drag breaks the stack.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import JsonSocialPost from '@/pages/app/components/social-designs/json-templates/JsonSocialPost';
import type { TemplateElement } from '@/pages/app/components/social-designs/json-templates/types';
import type { SocialPostSlots } from '@/lib/api';
import { saveOverlay, type Post } from '@/lib/gallery';
import { deriveTextColor, sampleZone, dominantTextZone } from '@/lib/textOverlayColor';

// ── Shared font set (mirrors StudioFinishEditor's FONT_OPTIONS) ──────────────
const FONT_OPTIONS = [
  'Manrope', 'Inter', 'DM Sans', 'Archivo', 'Space Grotesk', 'Space Mono',
  'JetBrains Mono', 'Fraunces', 'Cormorant Garamond', 'Bodoni Moda',
  'Playfair Display', 'Instrument Serif', 'Bebas Neue', 'Anton',
  'Chivo', 'Work Sans', 'Syne', 'Libre Franklin', 'Cormorant', 'Fira Sans',
  'Eczar', 'Alegreya Sans', 'Alegreya', 'Source Sans Pro', 'Source Serif Pro',
  'Roboto', 'Inknut Antiqua', 'BioRhyme', 'Poppins', 'Archivo Narrow',
  'Libre Baskerville', 'Karla', 'Lora', 'Proza Libre', 'Spectral',
  'IBM Plex Sans', 'Montserrat', 'Lato', 'PT Sans', 'PT Serif', 'Cardo',
  'Neuton', 'Rubik', 'Open Sans', 'Inconsolata', 'Raleway', 'Merriweather',
];

const WEIGHT_OPTIONS: { v: number; l: string }[] = [
  { v: 400, l: 'Reg' },
  { v: 500, l: 'Med' },
  { v: 600, l: 'Semi' },
  { v: 700, l: 'Bold' },
  { v: 900, l: 'Black' },
];

const PLACEMENT_CELLS = [
  'top-left', 'top-center', 'top-right',
  'middle-left', 'middle-center', 'middle-right',
  'bottom-left', 'bottom-center', 'bottom-right',
];
const ANCHOR_INSET = 64;

// App design-system tokens (same as StudioFinishEditor).
const eyebrowClass = 'text-[10px] font-bold uppercase tracking-[0.18em] text-dark-muted';
const actionPillClass = 'h-11 px-4 inline-flex items-center justify-center gap-1.5 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark text-xs font-bold uppercase tracking-[0.08em] transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
const chipClass = (active: boolean) => `
  w-9 h-9 inline-flex items-center justify-center rounded-lg border text-sm transition-all cursor-pointer
  ${active
    ? 'bg-primary-light text-primary border-primary'
    : 'bg-white border-surface-dark/40 text-dark hover:border-primary/40'}
`;
const railRowClass = (selected: boolean) => `
  flex-1 min-w-0 text-left text-xs font-bold uppercase tracking-wide truncate transition-colors cursor-pointer
  ${selected ? 'text-primary' : 'text-dark'}
`;
const railGhostClass = 'w-full text-left px-3 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide border-2 border-dashed border-surface-dark/60 text-dark-muted hover:border-primary/50 hover:text-primary transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed';

type AnyEl = TemplateElement & Record<string, unknown>;

/** Human label for a slot key (headline → "Headline"). */
function slotLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Normalize a fontWeight (number | 'bold' | …) → number for chip-active checks. */
function weightNum(w: number | string | undefined): number {
  if (typeof w === 'number') return w;
  if (w === 'bold') return 700;
  if (w === 'semibold') return 600;
  if (w === 'medium') return 500;
  if (w === 'normal') return 400;
  const n = Number(w);
  return Number.isFinite(n) ? n : 400;
}

/** Display label for a layer row. */
function elLabel(el: AnyEl, slots: Record<string, string>): string {
  if (el.type === 'button') return 'CTA';
  const key = el.slotKey as string | undefined;
  if (key) return slotLabel(key);
  const txt = ((el.staticText as string) || '').trim();
  return txt ? txt.slice(0, 22) : 'Text';
}

export default function OverlayEditor({
  post,
  onSaved,
}: {
  post: Post;
  onSaved: (updated: Post) => void;
}) {
  const doc = post.text_overlay!; // caller guarantees presence
  const sceneUrl = post.base_image_url || post.image_url || '';

  // Local editable copy — structuredClone so we never mutate the post's doc.
  const [template, setTemplate] = useState(
    () => structuredClone(doc.template) as { canvas?: { width?: number; height?: number }; elements?: AnyEl[] },
  );
  const [slots, setSlots] = useState<Record<string, string>>(() => ({ ...(doc.slots || {}) }));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const canvasW = template.canvas?.width || 1024;
  const canvasH = template.canvas?.height || 1024;
  const aspect = canvasW / canvasH;

  const elements = useMemo<AnyEl[]>(() => (template.elements ?? []) as AnyEl[], [template]);
  // Shapes/lines: shown in the rail with a delete button only (no drag, no
  // property editing) — the user can remove a stray scrim/line but not tweak it.
  const shapeElements = useMemo(
    () => elements.filter((el) => el.type === 'shape'),
    [elements],
  );
  const editableElements = useMemo(
    () => elements.filter((el) => el.type === 'text' || el.type === 'button'),
    [elements],
  );
  const selected = useMemo(
    () => elements.find((el) => el.id === selectedId),
    [elements, selectedId],
  );
  const hasCta = useMemo(() => elements.some((el) => el.type === 'button'), [elements]);

  // ── Scene pixels — for the editor "From scene" swatches + add-element default
  //    colors. Computed with the SAME math the bake uses (textOverlayColor) so a
  //    locked color the user picks here is exactly what the re-bake keeps. ──
  const [sceneData, setSceneData] = useState<{ data: Uint8ClampedArray; W: number; H: number } | null>(null);
  useEffect(() => {
    if (!sceneUrl || typeof document === 'undefined') return;
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      const S = 160;
      const cv = document.createElement('canvas');
      cv.width = S;
      cv.height = S;
      const ctx = cv.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, S, S);
      try {
        setSceneData({ data: ctx.getImageData(0, 0, S, S).data, W: S, H: S });
      } catch {
        setSceneData(null); // tainted canvas — fall back to neutral defaults
      }
    };
    img.onerror = () => !cancelled && setSceneData(null);
    img.src = sceneUrl;
    return () => {
      cancelled = true;
    };
  }, [sceneUrl]);

  const sceneColor = (zone: 'top' | 'center' | 'bottom' | null): string =>
    sceneData
      ? deriveTextColor(sampleZone(sceneData.data, sceneData.W, sceneData.H, zone))
      : '#111111';

  const bgSwatches = useMemo(() => {
    if (!sceneData) return [] as string[];
    const out = (['top', 'center', 'bottom'] as const).map((z) =>
      deriveTextColor(sampleZone(sceneData.data, sceneData.W, sceneData.H, z)),
    );
    return [...new Set(out)];
  }, [sceneData]);

  // ── Stage → fit box (object-contain), same pattern as StudioFinishEditor ──
  const stageRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setStageSize({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const boxW =
    stageSize.w > 0 && stageSize.h > 0
      ? Math.floor(Math.min(stageSize.w, stageSize.h * aspect))
      : stageSize.w || 0;
  const boxH = Math.round(boxW / aspect);
  const scale = boxW ? boxW / canvasW : 0;

  // ── Drag boxes from the live DOM rects (the flow layout the bake screenshots) ──
  const [boxes, setBoxes] = useState<Map<string, { x: number; y: number; w: number; h: number }>>(
    () => new Map(),
  );
  useLayoutEffect(() => {
    const host = previewRef.current;
    if (!host || !scale) return;
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const hostRect = host.getBoundingClientRect();
      const next = new Map<string, { x: number; y: number; w: number; h: number }>();
      host.querySelectorAll<HTMLElement>('[data-eid]').forEach((node) => {
        const id = node.dataset.eid;
        if (!id) return;
        const r = node.getBoundingClientRect();
        next.set(id, {
          x: (r.left - hostRect.left) / scale,
          y: (r.top - hostRect.top) / scale,
          w: r.width / scale,
          h: r.height / scale,
        });
      });
      setBoxes(next);
    };
    const raf = requestAnimationFrame(() => requestAnimationFrame(measure));
    if (document.fonts?.ready) void document.fonts.ready.then(measure);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [template, slots, scale]);

  // ── Mutators ──
  const updateElement = (id: string, patch: Record<string, unknown>) => {
    setTemplate((prev) => {
      const next = structuredClone(prev);
      const el = (next.elements ?? []).find((e) => e.id === id);
      if (el) Object.assign(el, patch);
      return next;
    });
    setDirty(true);
  };

  const deleteElement = (id: string) => {
    setTemplate((prev) => {
      const next = structuredClone(prev);
      next.elements = (next.elements ?? []).filter((e) => e.id !== id);
      return next;
    });
    if (selectedId === id) setSelectedId(null);
    setDirty(true);
  };

  const setSlot = (key: string, value: string) => {
    setSlots((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  // Edit the selected element's text — slot-bound writes the slot, else staticText.
  const setText = (el: AnyEl, value: string) => {
    const key = el.slotKey as string | undefined;
    if (key) setSlot(key, value);
    else updateElement(el.id, { staticText: value });
  };
  const textValue = (el: AnyEl): string => {
    const key = el.slotKey as string | undefined;
    if (key) return slots[key] ?? '';
    return (el.staticText as string) ?? '';
  };

  // Manual color always wins on re-bake → stamp lockColor.
  const setColorLocked = (el: AnyEl, patch: Record<string, unknown>) =>
    updateElement(el.id, { ...patch, lockColor: true });

  // Anchor to a canvas corner/edge using the element's MEASURED box.
  const applyAnchor = (el: AnyEl, pos: string) => {
    const box = boxes.get(el.id);
    const bw = box ? box.w : ((el.width as number) ?? 300);
    const bh = box ? box.h : ((el.height as number) ?? ((el.fontSize as number) ?? 32) * 1.3);
    const [v, h] = pos.split('-');
    const x =
      h === 'left' ? ANCHOR_INSET
        : h === 'right' ? Math.round(canvasW - ANCHOR_INSET - bw)
          : Math.round((canvasW - bw) / 2);
    const y =
      v === 'top' ? ANCHOR_INSET
        : v === 'bottom' ? Math.round(canvasH - ANCHOR_INSET - bh)
          : Math.round((canvasH - bh) / 2);
    const patch: Record<string, unknown> = { x, y, stackOnPrevious: false };
    if (el.type === 'text') {
      patch.textAlign = h === 'left' ? 'left' : h === 'right' ? 'right' : 'center';
    }
    updateElement(el.id, patch);
  };

  // ── Add-element factories ──
  const addText = () => {
    const id = `text-${Date.now().toString(36)}`;
    const width = Math.round(canvasW * 0.8);
    const el: AnyEl = {
      id,
      type: 'text',
      x: Math.round((canvasW - width) / 2),
      y: Math.round(canvasH * 0.42),
      width,
      staticText: 'New text',
      fontFamily: 'Manrope',
      fontSize: Math.round(canvasW * 0.055),
      fontWeight: 700,
      color: sceneColor(dominantTextZone(template as never) ?? 'center'),
      textAlign: 'center',
      lineHeight: 1.15,
      lockColor: true,
    } as unknown as AnyEl;
    setTemplate((prev) => {
      const next = structuredClone(prev);
      next.elements = [...(next.elements ?? []), el];
      return next;
    });
    setSelectedId(id);
    setDirty(true);
  };

  const addCta = () => {
    if (hasCta) return;
    const id = `cta-${Date.now().toString(36)}`;
    const width = Math.round(canvasW * 0.36);
    const height = Math.round(canvasH * 0.09);
    const accent = sceneColor(null);
    const el: AnyEl = {
      id,
      type: 'button',
      x: Math.round((canvasW - width) / 2),
      y: Math.round(canvasH - ANCHOR_INSET - height),
      width,
      height,
      slotKey: 'cta',
      staticText: 'Shop Now',
      backgroundColor: 'transparent',
      textColor: accent,
      borderColor: accent,
      borderWidth: 2,
      outline: true,
      fontFamily: 'Manrope',
      fontSize: Math.round(canvasH * 0.032),
      fontWeight: 700,
      borderRadius: Math.round(height / 2),
      paddingX: Math.round(width * 0.08),
      paddingY: Math.round(height * 0.22),
      lockColor: true,
    } as unknown as AnyEl;
    setTemplate((prev) => {
      const next = structuredClone(prev);
      next.elements = [...(next.elements ?? []), el];
      return next;
    });
    setSelectedId(id);
    setDirty(true);
  };

  // ── Drag (window-level so it survives leaving the handle) ──
  const dragRef = useRef<{ id: string; offX: number; offY: number } | null>(null);
  const startDrag = (e: React.MouseEvent, el: AnyEl) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(el.id);
    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect || !scale) return;
    const cx = (e.clientX - rect.left) / scale;
    const cy = (e.clientY - rect.top) / scale;
    const box = boxes.get(el.id);
    const ox = box ? box.x : (el.x as number);
    const oy = box ? box.y : (el.y as number);
    dragRef.current = { id: el.id, offX: cx - ox, offY: cy - oy };
  };
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const rect = previewRef.current?.getBoundingClientRect();
      if (!rect || !scale) return;
      const cx = (e.clientX - rect.left) / scale;
      const cy = (e.clientY - rect.top) / scale;
      const nx = Math.max(0, Math.min(canvasW, cx - drag.offX));
      const ny = Math.max(0, Math.min(canvasH, cy - drag.offY));
      // Dragging breaks the auto-stack so the dropped y is authoritative.
      updateElement(drag.id, { x: Math.round(nx), y: Math.round(ny), stackOnPrevious: false });
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // updateElement uses functional setState only → safe to omit.
  }, [scale, canvasW, canvasH]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await saveOverlay(
        post.id,
        slots,
        template as unknown as Record<string, unknown>,
      );
      setDirty(false);
      onSaved(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Color row (B&W + From-scene swatches + native picker) ──
  // Plain render-functions (called, not mounted as <Components>) so a re-render
  // from a slider/color change doesn't remount + interrupt the input.
  const colorRow = ({
    label,
    value,
    onPick,
  }: {
    label: string;
    value: string;
    onPick: (hex: string) => void;
  }) => {
    const swatch = (hex: string) => (
      <button
        key={hex}
        type="button"
        onClick={() => onPick(hex)}
        title={hex}
        className={`w-7 h-7 rounded-md border transition-all ${
          value?.toLowerCase() === hex.toLowerCase()
            ? 'border-primary ring-2 ring-primary/30'
            : 'border-surface-dark/40 hover:border-primary/50'
        }`}
        style={{ backgroundColor: hex }}
      />
    );
    return (
      <div className="space-y-1.5">
        <p className={eyebrowClass}>{label}</p>
        <div className="flex flex-wrap items-center gap-1.5">
          {swatch('#ffffff')}
          {swatch('#000000')}
          {bgSwatches.map(swatch)}
          <label
            className="w-7 h-7 rounded-md border border-surface-dark/40 hover:border-primary/50 cursor-pointer overflow-hidden relative"
            title="Custom color"
          >
            <span className="absolute inset-0 flex items-center justify-center text-dark-muted text-sm pointer-events-none">
              <i className="ri-palette-line" />
            </span>
            <input
              type="color"
              value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : '#ffffff'}
              onChange={(e) => onPick(e.target.value)}
              className="absolute -inset-2 opacity-0 cursor-pointer"
            />
          </label>
        </div>
      </div>
    );
  };

  // ── Range field ──
  const rangeField = ({
    label,
    value,
    min,
    max,
    step = 1,
    onChange,
    suffix = '',
  }: {
    label: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    onChange: (v: number) => void;
    suffix?: string;
  }) => (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <p className={eyebrowClass}>{label}</p>
        <span className="text-xs font-bold text-dark tabular-nums">
          {Math.round(value)}
          {suffix}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-primary cursor-pointer"
      />
    </div>
  );

  // ── Anchor grid (shared by text + button) ──
  const anchorGrid = (el: AnyEl) => (
    <div className="space-y-1.5">
      <p className={eyebrowClass}>Placement</p>
      <div className="grid grid-cols-3 gap-1.5 w-[120px]">
        {PLACEMENT_CELLS.map((pos) => (
          <button
            key={pos}
            type="button"
            onClick={() => applyAnchor(el, pos)}
            title={pos.replace('-', ' ')}
            className="aspect-square rounded-md border border-surface-dark/40 bg-white hover:border-primary hover:bg-primary-light flex items-center justify-center transition-all"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-dark-muted" />
          </button>
        ))}
      </div>
    </div>
  );

  const sel = selected as AnyEl | undefined;
  const isText = sel?.type === 'text';
  const isBtn = sel?.type === 'button';
  const btnOutline = isBtn && !!sel?.outline;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,843px)_589px] gap-5 items-stretch xl:h-[800px]">
      {/* LEFT — stage (filled, image fits inside via object-contain math) */}
      <div ref={stageRef} className="relative order-1 min-h-[440px] xl:min-h-0 flex items-center justify-center">
        {boxW > 0 && (
          <div
            ref={previewRef}
            className="relative overflow-hidden rounded-2xl border border-surface-dark/30 bg-white shadow-sm"
            style={{ width: boxW, height: boxH }}
          >
            {scale > 0 && sceneUrl && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                }}
              >
                <JsonSocialPost
                  config={template as never}
                  slots={slots as unknown as SocialPostSlots}
                  productImageUrl=""
                  mergedSceneUrl={sceneUrl}
                />
              </div>
            )}

            {/* Drag handles — one per text/CTA element at its MEASURED render box. */}
            <div className="absolute inset-0">
              {editableElements.map((el) => {
                const box = boxes.get(el.id);
                if (!box) return null;
                const isSel = selectedId === el.id;
                return (
                  <div
                    key={el.id}
                    onMouseDown={(e) => startDrag(e, el)}
                    style={{
                      position: 'absolute',
                      left: box.x * scale,
                      top: box.y * scale,
                      width: box.w * scale,
                      height: box.h * scale,
                      cursor: 'move',
                      borderRadius: 4,
                    }}
                    className={
                      isSel
                        ? 'border-2 border-primary bg-primary/10'
                        : 'border border-dashed border-primary/30 hover:border-primary hover:bg-primary/5'
                    }
                  />
                );
              })}
            </div>

            {/* Save-status pill (top-right of the image). */}
            <div className="absolute top-3 right-3">
              {saving ? (
                <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-white/90 border border-surface-dark/40 text-dark-muted backdrop-blur-sm">
                  <i className="ri-loader-4-line animate-spin mr-1" />
                  Saving…
                </span>
              ) : error ? (
                <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-red-50 border border-red-200 text-red-700 backdrop-blur-sm">
                  Save failed
                </span>
              ) : dirty ? (
                <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-50 border border-amber-200 text-amber-700 backdrop-blur-sm">
                  Unsaved edits
                </span>
              ) : (
                <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 border border-emerald-200 text-emerald-700 backdrop-blur-sm">
                  Saved
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* RIGHT — controls panel */}
      <div className="order-2 rounded-2xl bg-white shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] p-5 flex flex-col gap-4 min-h-0">
        <div className="flex gap-3 flex-1 min-h-0">
          {/* Layer rail */}
          <div className="w-[170px] flex-shrink-0 flex flex-col gap-2 overflow-y-auto sidebar-scroll">
            <p className={eyebrowClass}>Layers</p>
            {editableElements.map((el) => {
              const isSel = selectedId === el.id;
              return (
                <div
                  key={el.id}
                  onMouseDown={() => setSelectedId(el.id)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-all cursor-pointer ${
                    isSel
                      ? 'bg-primary-light border-primary/40'
                      : 'bg-surface border-transparent hover:bg-surface-dark/40'
                  }`}
                >
                  <i
                    className={`text-sm flex-shrink-0 ${
                      el.type === 'button' ? 'ri-cursor-line' : 'ri-text'
                    } ${isSel ? 'text-primary' : 'text-dark-muted'}`}
                  />
                  <span className={railRowClass(isSel)}>{elLabel(el, slots)}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteElement(el.id);
                    }}
                    title="Delete layer"
                    className="text-dark-muted hover:text-red-600 transition-colors flex-shrink-0"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              );
            })}

            {/* Shapes/lines — label + delete only (not selectable, no editing). */}
            {shapeElements.map((el) => {
              const sh = el as unknown as { shape?: string; height?: number };
              const label = sh.shape === 'circle'
                ? 'Circle'
                : (typeof sh.height === 'number' && sh.height <= 6) ? 'Line' : 'Shape';
              return (
                <div
                  key={el.id}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-transparent bg-surface"
                >
                  <i className="ri-shape-line text-sm flex-shrink-0 text-dark-muted" />
                  <span className={railRowClass(false)}>{label}</span>
                  <button
                    type="button"
                    onClick={() => deleteElement(el.id)}
                    title="Delete"
                    className="text-dark-muted hover:text-red-600 transition-colors flex-shrink-0"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              );
            })}

            <button type="button" onClick={addText} className={railGhostClass}>
              <i className="ri-add-line mr-1" />
              Text
            </button>
            {!hasCta && (
              <button type="button" onClick={addCta} className={railGhostClass}>
                <i className="ri-add-line mr-1" />
                CTA
              </button>
            )}
          </div>

          {/* Property panel */}
          <div className="flex-1 min-w-0 rounded-xl bg-surface/60 border border-surface-dark/30 p-4 flex flex-col gap-4 overflow-y-auto sidebar-scroll">
            {!sel && (
              <p className="text-xs text-dark-muted m-auto text-center px-4">
                Select a layer to edit, or add text / a CTA from the left.
              </p>
            )}

            {sel && (
              <>
                {/* Content */}
                <div className="space-y-1.5">
                  <p className={eyebrowClass}>{isBtn ? 'Button label' : 'Text content'}</p>
                  <textarea
                    value={textValue(sel)}
                    onChange={(e) => setText(sel, e.target.value)}
                    rows={isBtn ? 1 : 2}
                    className="w-full rounded-lg border border-surface-dark/40 px-3 py-2 text-sm focus:border-primary focus:outline-none resize-y bg-white"
                  />
                </div>

                {/* Font */}
                <div className="space-y-1.5">
                  <p className={eyebrowClass}>Font</p>
                  <select
                    value={(sel.fontFamily as string) || 'Manrope'}
                    onChange={(e) => updateElement(sel.id, { fontFamily: e.target.value })}
                    className="w-full rounded-lg border border-surface-dark/40 px-3 py-2 text-sm focus:border-primary focus:outline-none bg-white"
                    style={{ fontFamily: (sel.fontFamily as string) || 'Manrope' }}
                  >
                    {FONT_OPTIONS.map((f) => (
                      <option key={f} value={f} style={{ fontFamily: f }}>
                        {f}
                      </option>
                    ))}
                    {!FONT_OPTIONS.includes((sel.fontFamily as string) || '') && (
                      <option value={sel.fontFamily as string}>
                        {(sel.fontFamily as string) || ''} (current)
                      </option>
                    )}
                  </select>
                </div>

                {/* Size */}
                {rangeField({
                  label: 'Font size',
                  value: (sel.fontSize as number) || 32,
                  min: 8,
                  max: isBtn ? 120 : 240,
                  onChange: (v) => updateElement(sel.id, { fontSize: v }),
                })}

                {/* Weight */}
                <div className="space-y-1.5">
                  <p className={eyebrowClass}>Weight</p>
                  <div className="flex flex-wrap gap-1.5">
                    {WEIGHT_OPTIONS.map((w) => (
                      <button
                        key={w.v}
                        type="button"
                        onClick={() => updateElement(sel.id, { fontWeight: w.v })}
                        className={`px-2.5 h-9 inline-flex items-center justify-center rounded-lg border text-xs font-bold transition-all cursor-pointer ${
                          weightNum(sel.fontWeight as number | string) === w.v
                            ? 'bg-primary-light text-primary border-primary'
                            : 'bg-white border-surface-dark/40 text-dark hover:border-primary/40'
                        }`}
                      >
                        {w.l}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Text-only: align, case, italic, placement, color */}
                {isText && (
                  <>
                    <div className="flex flex-wrap gap-6">
                      <div className="space-y-1.5">
                        <p className={eyebrowClass}>Align</p>
                        <div className="flex gap-1.5">
                          {(['left', 'center', 'right'] as const).map((a) => (
                            <button
                              key={a}
                              type="button"
                              onClick={() => updateElement(sel.id, { textAlign: a })}
                              className={chipClass(
                                (sel.textAlign as string) === a || (!sel.textAlign && a === 'left'),
                              )}
                            >
                              <i className={`ri-align-${a}`} />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <p className={eyebrowClass}>Style</p>
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            onClick={() => updateElement(sel.id, { italic: !sel.italic })}
                            className={chipClass(!!sel.italic)}
                          >
                            <i className="ri-italic" />
                          </button>
                          <button
                            type="button"
                            onClick={() => updateElement(sel.id, { uppercase: !sel.uppercase })}
                            className={chipClass(!!sel.uppercase)}
                            title="Uppercase"
                          >
                            <span className="text-[11px] font-bold">AA</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {anchorGrid(sel)}

                    {colorRow({
                      label: 'Color',
                      value: (sel.color as string) || '',
                      onPick: (hex) => setColorLocked(sel, { color: hex }),
                    })}
                  </>
                )}

                {/* Button-only: style, color, radius, padding, placement */}
                {isBtn && (
                  <>
                    <div className="space-y-1.5">
                      <p className={eyebrowClass}>Button style</p>
                      <div className="flex gap-1.5">
                        {([
                          { v: false, l: 'Fill' },
                          { v: true, l: 'Outline' },
                        ] as const).map((s) => (
                          <button
                            key={String(s.v)}
                            type="button"
                            onClick={() => updateElement(sel.id, { outline: s.v })}
                            className={`px-3 h-9 inline-flex items-center justify-center rounded-lg border text-xs font-bold transition-all cursor-pointer ${
                              !!sel.outline === s.v
                                ? 'bg-primary-light text-primary border-primary'
                                : 'bg-white border-surface-dark/40 text-dark hover:border-primary/40'
                            }`}
                          >
                            {s.l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {btnOutline
                      ? colorRow({
                          label: 'Text & border',
                          value: (sel.textColor as string) || '',
                          onPick: (hex) => setColorLocked(sel, { textColor: hex, borderColor: hex }),
                        })
                      : (
                        <>
                          {colorRow({
                            label: 'Fill',
                            value: (sel.backgroundColor as string) || '',
                            onPick: (hex) => setColorLocked(sel, { backgroundColor: hex }),
                          })}
                          {colorRow({
                            label: 'Text',
                            value: (sel.textColor as string) || '',
                            onPick: (hex) => setColorLocked(sel, { textColor: hex }),
                          })}
                        </>
                      )}

                    {rangeField({
                      label: 'Corner radius',
                      value: (sel.borderRadius as number) || 0,
                      min: 0,
                      max: Math.round(((sel.height as number) || 96) / 2),
                      onChange: (v) => updateElement(sel.id, { borderRadius: v }),
                    })}
                    <div className="flex gap-3">
                      <div className="flex-1">
                        {rangeField({
                          label: 'Pad X',
                          value: (sel.paddingX as number) || 0,
                          min: 0,
                          max: 120,
                          onChange: (v) => updateElement(sel.id, { paddingX: v }),
                        })}
                      </div>
                      <div className="flex-1">
                        {rangeField({
                          label: 'Pad Y',
                          value: (sel.paddingY as number) || 0,
                          min: 0,
                          max: 80,
                          onChange: (v) => updateElement(sel.id, { paddingY: v }),
                        })}
                      </div>
                    </div>

                    {anchorGrid(sel)}
                  </>
                )}

                {/* Opacity (universal) */}
                {rangeField({
                  label: 'Opacity',
                  value: Math.round(((sel.opacity as number) ?? 1) * 100),
                  min: 0,
                  max: 100,
                  suffix: '%',
                  onChange: (v) => updateElement(sel.id, { opacity: v / 100 }),
                })}
              </>
            )}
          </div>
        </div>

        {error && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        {/* Action row */}
        <div className="flex items-center justify-end gap-2.5">
          <a href={post.image_url ?? '#'} download className={actionPillClass}>
            <i className="ri-download-2-line" />
            Download
          </a>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty}
            className="h-11 px-5 inline-flex items-center justify-center gap-1.5 rounded-full bg-primary text-white text-xs font-bold uppercase tracking-[0.08em] hover:bg-primary-hover disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <i className="ri-save-line" />
                Save edits
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
